import { useInterval } from 'hooks/usePolling';
import { useStore } from 'hooks/useStore';
import { LvTime, LvTimePayload } from 'Interfaces/ReferenceInterfaces/LvTimeInterface';
import { DataTableStateEvent } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';

interface FormData {
  roomTypeId: number;
  bottleCountMin: number;
  bottleCountMax: number;
  dosageForm: string;
  incomingJobTypeId: number;
  lvTimeMinutes: number;
}

export const useLvTimeTable = (pageSize: number = 10000) => {
  const { lvTimeStore, roomStore, runTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<LvTime | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemMenu = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  const dosageFormOptions = [
    {
      label: 'Capsule',
      value: 'Capsule'
    },
    {
      label: 'Tablet',
      value: 'Tablet'
    },
    {
      label: 'Any',
      value: 'Any'
    }
  ];

  // Load initial data when component mounts
  useEffect(() => {
    lvTimeStore.fetchLvTimes(currentPage, pageSize);
    roomStore.fetchRooms();
    roomStore.fetchRoomTypes();
    runTypeStore.fetchRunTypes();
  }, [currentPage, pageSize]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await lvTimeStore.pollLvTimes(currentPage, pageSize);
      await roomStore.pollRoomTypes();
    } catch (error) {
      // Don't break the polling cycle on error
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for LV Time data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1; // PrimeReact uses 0-based indexing, our API uses 1-based
      setCurrentPage(newPage);
      lvTimeStore.fetchLvTimes(newPage, 5);
    }
  };

  // Prepare room options from roomStore
  const roomOptions = roomStore.rooms.map(room => ({
    label: room.room_short ? `${room.room_short} - ${room.room_long}` : room.room_long || '',
    value: room.id
  }));

  // Prepare room type options from roomStore room types
  const roomTypeOptions = roomStore.roomTypeOptions;

  // Prepare incoming job type options from runTypeStore
  const incomingJobTypeOptions = runTypeStore.runTypes.map(runType => ({
    label: runType.description || '',
    value: runType.id
  }));

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      // Edit existing row
      const updatePayload: LvTimePayload = {
        Room_Type: formData.roomTypeId,
        bottle_count_min: formData.bottleCountMin,
        bottle_count_max: formData.bottleCountMax,
        dosage_form: formData.dosageForm,
        incoming_job_type: formData.incomingJobTypeId,
        lv_time_minutes: formData.lvTimeMinutes
      };

      const success = await lvTimeStore.updateLvTime(selectedRow.id, updatePayload);
      if (!success) {
        throw new Error('Failed to update LV time');
      }
    } else {
      // Add new row
      const createPayload: LvTimePayload = {
        Room_Type: formData.roomTypeId,
        incoming_job_type: formData.incomingJobTypeId,
        lv_time_minutes: formData.lvTimeMinutes,
        // Add the missing fields from the interface
        bottle_count_min: formData.bottleCountMin,
        bottle_count_max: formData.bottleCountMax,
        dosage_form: formData.dosageForm,
        flag: true
      };

      const success = await lvTimeStore.createLvTime(createPayload);
      if (!success) {
        throw new Error('Failed to create LV time');
      }
    }
    // Store methods already fetch updated data - no need to fetch again
    // Don't clear selectedRow here - let the modal's onHide handle cleanup
  };

  // Update the handleDeleteRow function
  const handleDeleteRow = async () => {
    if (selectedRow) {
      await lvTimeStore.deleteLvTime(selectedRow.id);
      setSelectedRow(null);
      setShowDeleteModal(false);
      // Refresh data while maintaining current page
      await lvTimeStore.fetchLvTimes(currentPage, pageSize);
    }
  };
  const prepareFormData = (row: LvTime): FormData => {
    return {
      roomTypeId: row.Room_Type.id || 0,
      bottleCountMin: row.bottle_count_min || 0,
      bottleCountMax: row.bottle_count_max || 0,
      dosageForm: row.dosage_form || '',
      incomingJobTypeId: row.incoming_job_type.id || 0,
      lvTimeMinutes: row.lv_time_minutes || 0
    };
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const filteredLvTimeData = lvTimeStore.lvTimes.filter(item => {
    if (!searchQuery.trim()) {
      return true;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      item.Room_Type.description.toLowerCase().includes(searchLower) ||
      item.incoming_job_type.description.toLowerCase().includes(searchLower) ||
      item.dosage_form.toLowerCase().includes(searchLower) ||
      item.lv_time_minutes.toString().includes(searchQuery) ||
      item.bottle_count_min.toString().includes(searchQuery) ||
      item.bottle_count_max.toString().includes(searchQuery)
    );
  });

  const handleExportToExcel = () => {
    const exportData = filteredLvTimeData.map(item => ({
      'Room Type': item.Room_Type.description,
      'Bottle Count Min': item.bottle_count_min,
      'Bottle Count Max': item.bottle_count_max,
      'Dosage Form': item.dosage_form,
      'Incoming Job Type': item.incoming_job_type.description,
      'LV Time (mins)': item.lv_time_minutes
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'LV Times');
    XLSX.writeFile(workbook, 'lv_times_export.xlsx');
  };

  // Method to manually refresh data
  const refreshData = async () => {
    await lvTimeStore.refreshData(currentPage, pageSize);
  };

  return {
    lvTimeData: filteredLvTimeData,
    isLoading: lvTimeStore.isLoading || roomStore.roomTypesLoading,
    pagination: lvTimeStore.pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    prepareFormData,
    roomOptions,
    incomingJobTypeOptions,
    onPageChange,
    currentPage,
    searchQuery,
    handleSearchChange,
    handleExportToExcel,
    roomTypeOptions,
    refreshData,
    dosageFormOptions
  };
};
